import React from 'react';
import { defaultMemoize } from 'reselect';

import { Messages } from '../../../codegen/Localize';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { ButtonControlledChildComponentProps } from '../../../common/react/components/ButtonControlledComponent';
import { Icons } from '../../../common/react/Icons';
import { IExplorableItemTypeInfo } from '../../../ts/models/resourceInfoParts';
import { ExplorerDropdown } from '../Components/ExplorerDropdown';
import { ExplorerFlyoutPanelProps } from '../Components/ExplorerFlyoutPanel';
import { ExplorerToolbarTestIds } from '../Explorer/ExplorerToolbar';
import { getActionSectionsForItems } from './ActionsList';
import { ActionsMenuPanelOwnProps, ConnectedActionsMenuPanel } from './ConnectedActionsMenuPanel';
import { isUserAccessDeniedForTargetItem } from './EntityActions/IEntityActionHelpers';

export interface ActionsMenuProps {
  readonly disabled?: boolean; // passed through to ButtonControlledComponent, which defaults to false
  readonly targetItems: IExplorableItemTypeInfo[];
  readonly useIcon: boolean; // This will use the '...' icon and no caret instead of text
  readonly buttonTitle?: string;
  readonly tabIndex?: number;

  // The menu panel component is exposed only for mocking during tests, so that the tests don't
  // render the real connected menu panel, which is tested separately
  readonly menuPanelComponent?: React.ComponentType<ActionsMenuPanelOwnProps>;
}

export const ActionsMenuTestId = 'action-menu';

/**
 * The ActionsMenu is a menu component with a button whose icon resembles an ellipsis, which we use
 * in various places throughout the app to display lists of actions that can be performed on various
 * entities.
 */
export class ActionsMenu extends React.PureComponent<ActionsMenuProps> {

  private memoizedGetActionsMenuPanel = defaultMemoize(getActionMenuPanel);

  public render() {
    return (
      <ExplorerDropdown
        buttonTitle={this.props.buttonTitle || Messages.moreActions()}
        testId={ActionsMenuTestId}
        renderButtonContent={this.renderButtonContent}
        renderPanel={this.renderPanel}
        disabled={this.props.disabled || isUserAccessDeniedForTargetItem(this.props.targetItems)}
        panelTitle={Messages.actionsMenu()}
        buttonStyleOverrides={{
          minWidth: 0,
          verticalAlign: 'middle',
          height: 'auto',
          cursor: this.props.disabled ? undefined : 'pointer',
        }}
        renderButtonContentOnly={this.props.useIcon}
        tabIndex={this.props.tabIndex}
      />
    );
  }

  private renderButtonContent = (showingOrActive: boolean): JSX.Element => {
    if (this.props.useIcon) {
      const styleOverrides: React.CSSProperties = {
        opacity: this.props.disabled ? CommonStyles.Opacity.DisabledOpacity : undefined,
        verticalAlign: 'middle'
      };

      return (
        <Icons.actionIcon hovered={ showingOrActive }
                          suppressHover={ this.props.disabled }
                          style={ styleOverrides }
        />
      );
    } else {
      return <span>{Messages.actions()}</span>;
    }
  }

  private renderPanel = (props: ExplorerFlyoutPanelProps): JSX.Element => {
    return this.memoizedGetActionsMenuPanel(props, this.props);
  }
}

const getActionMenuPanel = (flyoutProps: ExplorerFlyoutPanelProps, props: ActionsMenuProps): JSX.Element => {
  const panelProps: ActionsMenuPanelOwnProps & ButtonControlledChildComponentProps = {
    targetItems: props.targetItems,
    entityActionSections: getActionSectionsForItems(...props.targetItems),
    anchorElement: flyoutProps.anchorEl,
    closeMe: flyoutProps.onCloseRequest,
    testId: flyoutProps.testId,
    id: `${flyoutProps.testId}-${ExplorerToolbarTestIds.ExplorerDropdownLabelSuffix}`,
    role: 'menu',
  };

  const MenuPanelComponent: React.ComponentType<ActionsMenuPanelOwnProps> = props.menuPanelComponent || ConnectedActionsMenuPanel;
  MenuPanelComponent.displayName = ActionsMenuPanelWrapperDisplayName;
  return <MenuPanelComponent {...panelProps} />;
};

export const ActionsMenuPanelWrapperDisplayName = 'ActionsMenuPanelWrapper';
